import { ref, computed, Ref, onMounted, onUnmounted } from 'vue'
import api from '@/api'
import { QueueStatusResponse, WhatsAppMessageDto, WhatsAppMessageItem } from '@/classes/WhatsApp'
import util from '@/helpers/util'

export function useWhatsAppMessages(
    getAccountName: (accountId: string) => string,
    getAccountPhoneNumber: (accountId: string) => string | undefined,
    getGroupName: (groupJid: string) => string,
    openConfigurations: Ref<Array<any>>,
    engineRunning: Ref<boolean>,
    convertUTCToEasternDateTimeLocal: (utcDateTime: string) => string,
    refreshQueueStatus?: () => Promise<void>
) {
    const allMessages = ref<QueueStatusResponse>({})
    const isRefreshingMessages = ref(false)
    const countdownTime = ref(new Date())

    let messagesPollTimeout: number | null = null
    let messagesPollingActive = false
    let countdownInterval: number | null = null

    const updateMessagesFromData = (serverMessages: QueueStatusResponse) => {
        // Ensure allMessages.value is initialized as an object
        if (!allMessages.value || typeof allMessages.value !== 'object') {
            allMessages.value = {}
        }

        const currentMessages = allMessages.value
        const existingMessagesMap = new Map<string, any>()
        
        // Add null check before Object.entries (check for null explicitly since typeof null === 'object')
        if (currentMessages != null && typeof currentMessages === 'object') {
            Object.entries(currentMessages).forEach(([accountId, items]: [string, any]) => {
                if (Array.isArray(items)) {
                    items.forEach((item: any) => {
                        const key = `${accountId}:${item.id}`
                        existingMessagesMap.set(key, item)
                    })
                }
            })
        }

        const serverMessageKeys = new Set<string>()
        // Add null check before Object.entries (check for null explicitly since typeof null === 'object')
        if (serverMessages != null && typeof serverMessages === 'object') {
            Object.entries(serverMessages).forEach(([accountId, items]: [string, WhatsAppMessageDto[]]) => {
                if (!Array.isArray(items)) return

                if (!allMessages.value[accountId]) {
                    allMessages.value[accountId] = []
                }

                items.forEach((serverItem: WhatsAppMessageDto) => {
                    const key = `${accountId}:${serverItem.id}`
                    serverMessageKeys.add(key)
                    const existingItem = existingMessagesMap.get(key)

                    if (!existingItem) {
                        allMessages.value[accountId].push(serverItem)
                    } else {
                        const index = allMessages.value[accountId].findIndex((m: WhatsAppMessageDto) => m.id === serverItem.id)
                        if (index !== -1) {
                            const currentItem = allMessages.value[accountId][index]
                            
                            if( currentItem.status === 'scheduled' && serverItem.status === 'scheduled' && currentItem.scheduledAt !== serverItem.scheduledAt) {
                                allMessages.value[accountId][index] = serverItem
                            }
                            else if (currentItem.status !== serverItem.status) {
                                allMessages.value[accountId][index] = serverItem
                            }
                        }
                    }
                })
            })
        }

        Object.entries(allMessages.value).forEach(([accountId, items]: [string, WhatsAppMessageDto[]]) => {
            if (Array.isArray(items)) {
                allMessages.value[accountId] = items.filter((item: WhatsAppMessageDto) => {
                    const key = `${accountId}:${item.id}`
                    return serverMessageKeys.has(key)
                })
            }
        })
    }

    const refreshAllMessages = async () => {
        try {
            const response = await api.whatsapp.getMessages()
            const serverMessages = response.data as QueueStatusResponse
            updateMessagesFromData(serverMessages)
        } catch (error) {
            console.error('Failed to refresh messages:', error)
        }
    }

    const getCountdown = (scheduledAt: string): string => {
        if (!scheduledAt) return ''
        try {
            const scheduled = new Date(scheduledAt)
            const now = countdownTime.value
            const diff = scheduled.getTime() - now.getTime()

            if (diff <= 0) {
                return 'Past due'
            }

            const days = Math.floor(diff / (1000 * 60 * 60 * 24))
            const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
            const seconds = Math.floor((diff % (1000 * 60)) / 1000)

            if (days > 0) {
                return `${days}d ${hours}h ${minutes}m ${seconds}s`
            } else if (hours > 0) {
                return `${hours}h ${minutes}m ${seconds}s`
            } else if (minutes > 0) {
                return `${minutes}m ${seconds}s`
            } else {
                return `${seconds}s`
            }
        } catch {
            return ''
        }
    }

    const removeMessage = async (accountId: string, id: string) => {
        if (!confirm('Remove this message?')) return
        try {
            await api.whatsapp.removeFromQueue(accountId, id)
            util.showToast('Message removed', { icon: 'check_circle', iconClass: 'toast-icon-circle' })
        } catch (error) {
            console.error('Failed to remove message:', error)
            util.showToast('Failed to remove message', { icon: 'error', iconClass: 'toast-icon-circle' })
        }
    }

    const postScheduledMessageNow = async (accountId: string, id: string) => {
        if (!confirm('Post this scheduled message now? It will be moved to the queue immediately.')) return
        try {
            await api.whatsapp.postScheduledMessageNow(accountId, id)
            if (engineRunning.value && refreshQueueStatus) {
                await refreshQueueStatus()
            }
            util.showToast('Message moved to queue', { icon: 'check_circle', iconClass: 'toast-icon-circle' })
        } catch (error: any) {
            console.error('Failed to post scheduled message now:', error)
            const errorMessage = error.response?.data?.error || 'Failed to post scheduled message now'
            util.showToast(errorMessage, { icon: 'error', iconClass: 'toast-icon-circle' })
        }
    }

    const reAddToQueue = async (accountId: string, id: string) => {
        if (!confirm('Re-add this message to the queue?')) return
        try {
            await api.whatsapp.reAddToQueue(accountId, id)
            if (engineRunning.value && refreshQueueStatus) {
                await refreshQueueStatus()
            }
            util.showToast('Message re-added to queue', { icon: 'check_circle', iconClass: 'toast-icon-circle' })
        } catch (error: any) {
            console.error('Failed to re-add message to queue:', error)
            const errorMessage = error.response?.data?.error || 'Failed to re-add message to queue'
            util.showToast(errorMessage, { icon: 'error', iconClass: 'toast-icon-circle' })
        }
    }

    const checkMessageStatus = async (accountId: string, id: string) => {
        try {
            const response = await api.whatsapp.checkMessageStatus(accountId, id)
            if (response.data.success) {
                // Message was sent - refresh messages to show updated status
                await refreshAllMessages()
                util.showToast(response.data.message || 'Message was successfully sent', { icon: 'check_circle', iconClass: 'toast-icon-circle' })
            } else {
                // Message was not sent - show error
                const errorMsg = response.data.errorMessage || 'Message was not sent'
                util.showToast(errorMsg, { icon: 'error', iconClass: 'toast-icon-circle' })
            }
        } catch (error: any) {
            console.error('Failed to check message status:', error)
            const errorMessage = error.response?.data?.error || 'Failed to check message status'
            util.showToast(errorMessage, { icon: 'error', iconClass: 'toast-icon-circle' })
        }
    }

    const scheduleNextMessagesPoll = async () => {
        if (!messagesPollingActive) {
            return
        }

        messagesPollTimeout = window.setTimeout(async () => {
            if (!isRefreshingMessages.value) {
                isRefreshingMessages.value = true
                try {
                    await refreshAllMessages()
                } finally {
                    isRefreshingMessages.value = false
                    scheduleNextMessagesPoll()
                }
            } else {
                scheduleNextMessagesPoll()
            }
        }, 5000)
    }

    const startMessagesPolling = () => {
        messagesPollingActive = true
        if (!isRefreshingMessages.value) {
            isRefreshingMessages.value = true
            refreshAllMessages().finally(() => {
                isRefreshingMessages.value = false
                scheduleNextMessagesPoll()
            })
        } else {
            scheduleNextMessagesPoll()
        }
    }

    const stopMessagesPolling = () => {
        messagesPollingActive = false
        if (messagesPollTimeout) {
            clearTimeout(messagesPollTimeout)
            messagesPollTimeout = null
        }
    }

    const getGroupSubjectFromConfig = (item: any): string => {
        let groupSubject = ''
        if (item.configurationName) {
            const config = openConfigurations.value.find(c => c.name === item.configurationName)
            if (config) {
                const accountGroup = config.groups.find((g: any) => g.jid === item.groupJid)
                if (accountGroup?.subject) {
                    groupSubject = accountGroup.subject
                }
            }
        }
        return groupSubject
    }

    const scheduledItemsList = computed(() => {
        const items: WhatsAppMessageItem[] = []

        Object.entries(allMessages.value).forEach(([accountId, accountItems]: [string, any]) => {
            if (Array.isArray(accountItems)) {
                accountItems.forEach((item: any) => {
                    if (item.status !== 'scheduled') {
                        return
                    }
                    const groupSubject = getGroupSubjectFromConfig(item)
                    const scheduledAtLocal = convertUTCToEasternDateTimeLocal(item.scheduledAt)
                    items.push(new WhatsAppMessageItem({
                        id: item.id,
                        accountId,
                        accountName: getAccountName(accountId),
                        accountPhoneNumber: item.accountPhoneNumber || getAccountPhoneNumber(accountId),
                        groupJid: item.groupJid,
                        groupName: getGroupName(item.groupJid),
                        groupSubject,
                        message: item.message,
                        configurationName: item.configurationName || null,
                        status: item.status,
                        scheduledAt: item.scheduledAt,
                        scheduledAtLocal,
                        imageUrl: item.imageUrl || null,
                        documentUrl: item.documentUrl || null,
                        messageType: item.messageType
                    }))
                })
            }
        })

        return items
    })

    const sentItemsList = computed(() => {
        const items: WhatsAppMessageItem[] = []

        if (!allMessages.value || typeof allMessages.value !== 'object') {
            return items
        }

        Object.entries(allMessages.value).forEach(([accountId, accountItems]: [string, any]) => {
            if (Array.isArray(accountItems)) {
                accountItems.forEach((item: any) => {
                    if (item.status !== 'sent') {
                        return
                    }
                    const groupSubject = getGroupSubjectFromConfig(item)
                    items.push(new WhatsAppMessageItem({
                        id: item.id,
                        accountId,
                        accountName: getAccountName(accountId),
                        accountPhoneNumber: item.accountPhoneNumber || getAccountPhoneNumber(accountId),
                        groupJid: item.groupJid,
                        groupName: getGroupName(item.groupJid),
                        groupSubject,
                        message: item.message,
                        configurationName: item.configurationName || null,
                        status: item.status,
                        sentAt: item.sentAt || null,
                        errorMessage: item.errorMessage || null,
                        whatsAppMessageId: item.whatsAppMessageId || null,
                        imageUrl: item.imageUrl || null,
                        documentUrl: item.documentUrl || null,
                        messageType: item.messageType
                    }))
                })
            }
        })

        return items.sort((a, b) => {
            const aTime = a.sentAt
            const bTime = b.sentAt
            if (!aTime) return 1
            if (!bTime) return -1
            return new Date(bTime).getTime() - new Date(aTime).getTime()
        })
    })

    const deliveredItemsList = computed(() => {
        const items: WhatsAppMessageItem[] = []

        if (!allMessages.value || typeof allMessages.value !== 'object') {
            return items
        }

        Object.entries(allMessages.value).forEach(([accountId, accountItems]: [string, any]) => {
            if (Array.isArray(accountItems)) {
                accountItems.forEach((item: any) => {
                    if (item.status !== 'delivered') {
                        return
                    }
                    const groupSubject = getGroupSubjectFromConfig(item)
                    items.push(new WhatsAppMessageItem({
                        id: item.id,
                        accountId,
                        accountName: getAccountName(accountId),
                        accountPhoneNumber: item.accountPhoneNumber || getAccountPhoneNumber(accountId),
                        groupJid: item.groupJid,
                        groupName: getGroupName(item.groupJid),
                        groupSubject,
                        message: item.message,
                        configurationName: item.configurationName || null,
                        status: item.status,
                        sentAt: item.sentAt || null,
                        deliveredAt: item.deliveredAt || null,
                        errorMessage: item.errorMessage || null,
                        imageUrl: item.imageUrl || null,
                        documentUrl: item.documentUrl || null,
                        messageType: item.messageType
                    }))
                })
            }
        })

        return items.sort((a, b) => {
            const aTime = a.deliveredAt || a.sentAt
            const bTime = b.deliveredAt || b.sentAt
            if (!aTime) return 1
            if (!bTime) return -1
            return new Date(bTime).getTime() - new Date(aTime).getTime()
        })
    })

    const failedItemsList = computed(() => {
        const items: WhatsAppMessageItem[] = []

        if (!allMessages.value || typeof allMessages.value !== 'object') {
            return items
        }

        Object.entries(allMessages.value).forEach(([accountId, accountItems]: [string, any]) => {
            if (Array.isArray(accountItems)) {
                accountItems.forEach((item: any) => {
                    if (item.status !== 'failed') {
                        return
                    }
                    const groupSubject = getGroupSubjectFromConfig(item)
                    items.push(new WhatsAppMessageItem({
                        id: item.id,
                        accountId,
                        accountName: getAccountName(accountId),
                        accountPhoneNumber: item.accountPhoneNumber || getAccountPhoneNumber(accountId),
                        groupJid: item.groupJid,
                        groupName: getGroupName(item.groupJid),
                        groupSubject,
                        message: item.message,
                        configurationName: item.configurationName || null,
                        status: item.status || 'failed',
                        sentAt: item.sentAt || null,
                        failedAt: item.failedAt || null,
                        errorMessage: item.errorMessage || null,
                        imageUrl: item.imageUrl || null,
                        documentUrl: item.documentUrl || null,
                        messageType: item.messageType
                    }))
                })
            }
        })

        return items.sort((a, b) => {
            const aTime = a.failedAt || a.sentAt
            const bTime = b.failedAt || b.sentAt
            if (!aTime) return 1
            if (!bTime) return -1
            return new Date(bTime).getTime() - new Date(aTime).getTime()
        })
    })

    onMounted(() => {
        countdownInterval = window.setInterval(() => {
            countdownTime.value = new Date()
        }, 1000)
    })

    onUnmounted(() => {
        stopMessagesPolling()
        if (countdownInterval) {
            clearInterval(countdownInterval)
            countdownInterval = null
        }
    })

    return {
        allMessages,
        isRefreshingMessages,
        scheduledItemsList,
        sentItemsList,
        deliveredItemsList,
        failedItemsList,
        refreshAllMessages,
        updateMessagesFromData,
        removeMessage,
        postScheduledMessageNow,
        reAddToQueue,
        checkMessageStatus,
        getCountdown,
        startMessagesPolling,
        stopMessagesPolling
    }
}

